import React, { useEffect, useState } from "react"
import {
  useLocation,
  useParams,
  useNavigate
} from "react-router-dom"
import axios from "axios"
import Cookies from "js-cookie"
import {
  formatStockQuantity,
  formatRemainingTime,
  getFoodImageUrls,
  getFoodStatusLabel,
  getHyderabadPincodeArea,
  getStoredLimitedDeliveryPincode,
  getStockQuantity,
  isLimitedCategory,
  isHyderabadPincode,
  isOrderable,
  saveStoredLimitedDeliveryPincode
} from "../utils/foodAvailability"

const API = "https://food-backend-9733.onrender.com"

const ProductDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [food, setFood] = useState(null)
  const [cartItems, setCartItems] = useState({})
  const [loading, setLoading] = useState(true)
  const [loadingItemId, setLoadingItemId] = useState(null)

  const [requirement, setRequirement] = useState("")
  const [msg, setMsg] = useState("")

  const [selectedSize, setSelectedSize] = useState("")
  const [showRequirementBox, setShowRequirementBox] = useState(false)
  const [showProductDetails, setShowProductDetails] = useState(false)
  const [deliveryPincode, setDeliveryPincode] = useState("")
  const [showAvailabilityCheck, setShowAvailabilityCheck] =
    useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)

  const getToken = () => {
    try {
      return JSON.parse(
        Cookies.get("logincrd") || "{}"
      )?.token
    } catch {
      return null
    }
  }

  const getSelectedPrice = () => {
    if (!food) return 0
    const limitedProduct = isLimitedCategory(food.category)

    if (limitedProduct && selectedSize === "100g") {
      return food.price
    }

    if (selectedSize === "250g") {
      return limitedProduct
        ? food.price_250 || Number(food.price) * 2.5
        : food.price_250 || food.price
    }

    if (selectedSize === "500g") {
      return food.price_500 || Number(food.price) * 2
    }

    if (selectedSize === "1kg") {
      return food.price_1kg || Number(food.price) * 4
    }

    return limitedProduct ? food.price : food.price_250 || food.price
  }

  const loadFood = () => {
    axios.get(`${API}/foods/${id}`)
      .then(res => {
        setFood(res.data)
        setLoading(false)
      })
      .catch(err => {
        console.log(err)
        setLoading(false)
      })
  }

  const loadCart = async () => {
    const token = getToken()

    if (!token) {
      setCartItems({})
      return
    }

    try {
      const res = await axios.get(`${API}/cart`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      const obj = {}

      res.data.forEach(item => {
        const defaultSize = isLimitedCategory(item.category)
          ? "100g"
          : "250g"
        const key =
          `${item.food_id}_${item.selected_size || defaultSize}`

        obj[key] = item.quantity
      })

      setCartItems(obj)

    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    setShowProductDetails(false)
    setActiveImageIndex(0)
    loadFood()
    loadCart()
  }, [id])

  useEffect(() => {
    if (!food) return

    if (isLimitedCategory(food.category)) {
      setSelectedSize("100g")
      setShowRequirementBox(false)
      setRequirement("")
      const storedPincode = getStoredLimitedDeliveryPincode()

      setDeliveryPincode(storedPincode)
      setShowAvailabilityCheck(
        location.search.includes("checkAvailability=1") ||
        Boolean(storedPincode)
      )
      return
    }

    setSelectedSize("")
    setShowRequirementBox(false)
    setRequirement("")
    setDeliveryPincode("")
    setShowAvailabilityCheck(false)
  }, [food, location.search])

  useEffect(() => {
    if (
      food &&
      isLimitedCategory(food.category) &&
      isHyderabadPincode(deliveryPincode)
    ) {
      saveStoredLimitedDeliveryPincode(deliveryPincode)
    }
  }, [deliveryPincode, food])

  const productImages = getFoodImageUrls(
    food || {},
    API,
    "https://via.placeholder.com/800x450"
  )

  useEffect(() => {
    if (productImages.length <= 1) return undefined

    const timer = setInterval(() => {
      setActiveImageIndex(prev =>
        prev === productImages.length - 1 ? 0 : prev + 1
      )
    }, 3500)

    return () => clearInterval(timer)
  }, [food?.id, productImages.length])

  // ================= SEND CUSTOM REQUIREMENT =================

  const sendRequirementToAdmin = async (food_id) => {

    const token = getToken()

    await axios.post(
      `${API}/product-requirement`,
      {
        food_id,
        selected_size: "custom",
        requirement
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
  }

  // ================= ADD TO CART =================

  const addToCart = async (food_id) => {
    const cartSize = selectedSize ||
      (isLimitedCategory(food?.category) ? "100g" : "")

    if (
      isLimitedCategory(food?.category) &&
      !isHyderabadPincode(deliveryPincode)
    ) {
      setMsg("Order not available. 48 Hours and Weekly items are delivered only to Hyderabad pincodes.")
      return
    }

    if (!showRequirementBox && !isOrderable(food)) {
      setMsg("This item is currently out of stock")
      return
    }

    const token = getToken()

    if (!token) {
      navigate("/login")
      return
    }

    // ===== NOTHING SELECTED =====

    if (!cartSize && !showRequirementBox) {

      setMsg("Please choose one option")

      return
    }

    // ===== CUSTOM REQUIREMENT =====

    if (showRequirementBox) {

      if (!requirement.trim()) {

        setMsg(
          "Please enter your custom requirement"
        )

        return
      }

      setLoadingItemId(food_id)

      try {

        await sendRequirementToAdmin(food_id)

        setRequirement("")

        setMsg(
          "Custom request sent successfully. We will contact you soon."
        )

      } catch (err) {

        console.log(err)

        setMsg(
          "Custom request send failed"
        )
      }

      setLoadingItemId(null)

      return
    }

    // ===== NORMAL CART =====

    setLoadingItemId(food_id)

    try {

      await axios.post(
        `${API}/cart`,
        {
          food_id,
          selected_size: cartSize
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      await loadCart()
      window.dispatchEvent(new Event("srilekha-cart-updated"))

      setMsg("Added to cart")

    } catch (err) {

      console.log(err)

      setMsg(
        err.response?.data?.msg ||
        "Add to cart failed"
      )
    }

    setLoadingItemId(null)
  }

  // ================= INCREMENT =================

  const incrementQty = async (food_id) => {
    const cartSize = selectedSize ||
      (isLimitedCategory(food?.category) ? "100g" : "")

    const token = getToken()

    if (!token) {
      navigate("/login")
      return
    }

    setLoadingItemId(food_id)

    try {

      await axios.put(
        `${API}/cart/increment`,
        {
          food_id,
          selected_size: cartSize
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      await loadCart()
      window.dispatchEvent(new Event("srilekha-cart-updated"))

    } catch (err) {

      console.log(err)
    }

    setLoadingItemId(null)
  }

  // ================= DECREMENT =================

  const decrementQty = async (food_id) => {
    const cartSize = selectedSize ||
      (isLimitedCategory(food?.category) ? "100g" : "")

    const token = getToken()

    if (!token) {
      navigate("/login")
      return
    }

    setLoadingItemId(food_id)

    try {

      await axios.put(
        `${API}/cart/decrement`,
        {
          food_id,
          selected_size: cartSize
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      await loadCart()
      window.dispatchEvent(new Event("srilekha-cart-updated"))

    } catch (err) {

      console.log(err)
    }

    setLoadingItemId(null)
  }

  if (loading) {
    return (
      <div className="page-state">
        <h2>Loading...</h2>
      </div>
    )
  }

  if (!food) {
    return (
      <div className="page-state">
        <h2>Product not found</h2>
      </div>
    )
  }

  const limitedProduct = isLimitedCategory(food.category)
  const finalSize =
    selectedSize || (limitedProduct ? "100g" : "custom")

  const cartKey =
    `${food.id}_${finalSize}`

  const selectedCartQty =
    cartItems[cartKey]
  const orderable = isOrderable(food)
  const timerText = formatRemainingTime(food)
  const statusLabel = getFoodStatusLabel(food)
  const stockQuantity = getStockQuantity(food)
  const stockLabel = formatStockQuantity(
    food,
    "grams"
  )
  const deliveryArea = getHyderabadPincodeArea(deliveryPincode)
  const isPincodeComplete = deliveryPincode.length === 6
  const isLimitedDeliveryAvailable =
    !limitedProduct ||
    Boolean(deliveryArea)
  const sizeOptions = limitedProduct
    ? [
        {
          value: "100g",
          label: "100 grams",
          price: food.price
        },
        {
          value: "250g",
          label: "250 grams",
          price: food.price_250 || Number(food.price) * 2.5
        }
      ]
    : [
        {
          value: "250g",
          label: "250 grams",
          price: food.price_250 || food.price
        },
        {
          value: "500g",
          label: "500 grams",
          price: food.price_500 || Number(food.price) * 2
        },
        {
          value: "1kg",
          label: "1 kg",
          price: food.price_1kg || Number(food.price) * 4
        }
      ]
  const productDescription =
    food.description ||
    `${food.name} is a fresh homemade ${food.category || "food"} item prepared in small batches after order confirmation. It is made with quality ingredients, packed carefully and delivered based on your location.`
  const productDetailPoints =
    String(food.product_details || "")
      .split(/\r?\n/)
      .map(point => point.trim())
      .filter(Boolean)
  const fallbackProductDetailPoints = [
    "Fresh homemade food",
    "Prepared after order confirmation",
    "Best quality ingredients used",
    "Delivery available based on location"
  ]
  const displayProductDetailPoints =
    productDetailPoints.length > 0
      ? productDetailPoints
      : fallbackProductDetailPoints

  return (

    <div className="product-details-page">

      <button
        className="product-back-btn"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div className="product-details-card">

        <div className="product-image-wrap">
          <div className="product-image-carousel">
            <img
              src={productImages[activeImageIndex] || productImages[0]}
              alt={food.name}
              className="product-main-img"
              onError={(e) => {
                e.currentTarget.src = "/logo192.png"
              }}
            />

            {productImages.length > 1 && (
              <>
                <button
                  type="button"
                  className="product-image-nav product-image-prev"
                  aria-label="Previous image"
                  onClick={() =>
                    setActiveImageIndex(prev =>
                      prev === 0
                        ? productImages.length - 1
                        : prev - 1
                    )
                  }
                >
                  ‹
                </button>

                <button
                  type="button"
                  className="product-image-nav product-image-next"
                  aria-label="Next image"
                  onClick={() =>
                    setActiveImageIndex(prev =>
                      prev === productImages.length - 1
                        ? 0
                        : prev + 1
                    )
                  }
                >
                  ›
                </button>

                <div className="product-image-dots">
                  {productImages.map((image, index) => (
                    <button
                      type="button"
                      key={image}
                      className={
                        activeImageIndex === index
                          ? "product-image-dot active"
                          : "product-image-dot"
                      }
                      aria-label={`Show image ${index + 1}`}
                      onClick={() => setActiveImageIndex(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <span
            className={
              orderable
                ? "product-status-pill"
                : "product-status-pill out"
            }
          >
            {statusLabel}
          </span>
        </div>

        <div className="product-info-box">

          <h1>{food.name}</h1>

          <div className="product-meta-row">
            <span>
              {food.category || "homemade"}
            </span>

            {timerText && (
              <span>
                {timerText}
              </span>
            )}

            {limitedProduct && stockQuantity !== null && (
              <span>
                Stock: {stockLabel}
              </span>
            )}
          </div>

          {limitedProduct && (
            <div className="limited-stock-panel">
              {stockQuantity !== null && (
                <>
                  <span>
                    Available Stock
                  </span>

                  <b>
                    {stockLabel}
                  </b>
                </>
              )}

              <small>
                100g and 250g packs only
              </small>
            </div>
          )}

          {limitedProduct && (
            <>
              {!showAvailabilityCheck && (
                <button
                  type="button"
                  className="availability-open-btn"
                  onClick={() => setShowAvailabilityCheck(true)}
                >
                  Check Availability
                </button>
              )}

              {showAvailabilityCheck && (
                <div
                  className={
                    isLimitedDeliveryAvailable
                      ? "pincode-check-card"
                      : "pincode-check-card blocked"
                  }
                >
                  <label>
                    Check Hyderabad Delivery

                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength="6"
                      placeholder="Enter pincode"
                      value={deliveryPincode}
                      onChange={(e) => {
                        const nextPincode =
                          e.target.value.replace(/\D/g, "").slice(0, 6)

                        setDeliveryPincode(nextPincode)

                        if (isHyderabadPincode(nextPincode)) {
                          saveStoredLimitedDeliveryPincode(
                            nextPincode
                          )
                        }

                        setMsg("")
                      }}
                    />
                  </label>

                  {deliveryPincode && (
                    <div className="pincode-result">
                      <b>
                        {
                          deliveryArea
                            ? "Order Available"
                            : isPincodeComplete
                              ? "Order Not Available"
                              : "Enter 6 digit pincode"
                        }
                      </b>

                      <span>
                        {
                          deliveryArea
                            ? `${deliveryArea}, Hyderabad`
                            : isPincodeComplete
                              ? "Not delivered to this location"
                              : "Hyderabad pincode required"
                        }
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {
            showRequirementBox ? (

              <h2 className="custom-price-text">
                Price will be confirmed after contact
              </h2>

            ) : (

              <h2>
                ₹ {getSelectedPrice()}
              </h2>
            )
          }

          <p className="product-description">
            {productDescription}
          </p>

          <div className="product-extra-details">
            <button
              type="button"
              className="product-details-toggle"
              onClick={() =>
                setShowProductDetails(prev => !prev)
              }
              aria-expanded={showProductDetails}
            >
              <span>
                Product Details
              </span>

              <b>
                {showProductDetails ? "-" : "+"}
              </b>
            </button>

            {showProductDetails && (
              <div className="product-details-panel">
                {displayProductDetailPoints.map(point => (
                  <p key={point}>
                    {point}
                  </p>
                ))}

                {!orderable && (
                  <p className="out-of-stock-note">
                    This item is currently out of stock.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ================= OPTIONS ================= */}

          <div
            className={
              limitedProduct
                ? "grams-price-box limited-options"
                : "grams-price-box"
            }
          >

            <h3>
              Choose Option
            </h3>

            {sizeOptions.map(option => (
              <label
                className="size-option"
                key={option.value}
              >

                <input
                  type="radio"
                  name="product_option"

                  checked={
                    selectedSize === option.value
                  }

                  onChange={() => {

                    setSelectedSize(option.value)

                    setShowRequirementBox(false)

                    setRequirement("")

                    setMsg("")
                  }}
                />

                <span>
                  {option.label}
                </span>

                <b>
                  ₹ {option.price}
                </b>

              </label>
            ))}

            {!limitedProduct && (
              <label className="size-option">

                <input
                  type="radio"
                  name="product_option"

                  checked={showRequirementBox}

                  onChange={() => {

                    setSelectedSize("")

                    setShowRequirementBox(true)

                    setMsg("")
                  }}
                />

                <span>
                  Custom Requirement
                </span>

                <b>
                  We will contact you
                </b>

              </label>
            )}
          </div>

          {/* ================= REQUIREMENT BOX ================= */}

          {
            showRequirementBox && (

              <div className="requirement-box">

                <h3>
                  Write Your Requirement
                </h3>

                <textarea
                  placeholder="
Example:
750 grams,
less spicy,
more sweet,
delivery timing...
                  "

                  value={requirement}

                  onChange={(e) => {

                    setRequirement(
                      e.target.value
                    )

                    setMsg("")
                  }}
                />

              </div>
            )
          }

          {
            msg && (

              <p className="product-message">
                {msg}
              </p>
            )
          }

          {/* ================= CART ================= */}

          {
            limitedProduct &&
            !isLimitedDeliveryAvailable ? (
              <button
                className="product-add-cart-btn availability-required-btn"
                type="button"
                onClick={() => setShowAvailabilityCheck(true)}
                disabled={
                  showAvailabilityCheck &&
                  isPincodeComplete &&
                  !deliveryArea
                }
              >
                {
                  showAvailabilityCheck &&
                  isPincodeComplete &&
                  !deliveryArea
                    ? "Order Not Available"
                    : "Check Availability"
                }
              </button>
            ) :
            !showRequirementBox &&
            selectedCartQty ? (

              <div className="product-cart-control-bar">

                <button
                  onClick={() =>
                    decrementQty(food.id)
                  }

                  disabled={
                    loadingItemId === food.id
                  }
                >
                  -
                </button>

                <span>

                  {
                    loadingItemId === food.id
                      ? "Updating..."
                      : `Added ${selectedCartQty}`
                  }

                </span>

                <button
                  onClick={() =>
                    incrementQty(food.id)
                  }

                  disabled={
                    loadingItemId === food.id ||
                    !orderable
                  }
                >
                  +
                </button>

              </div>

            ) : (

              <button
                className="product-add-cart-btn"

                onClick={() =>
                  addToCart(food.id)
                }

                disabled={
                  loadingItemId === food.id ||
                  (!orderable && !showRequirementBox)
                }
              >

                {
                  !orderable && !showRequirementBox
                    ? statusLabel
                    :
                  loadingItemId === food.id
                    ? showRequirementBox
                      ? "Sending..."
                      : "Adding..."
                    : showRequirementBox
                      ? "Send Custom Request"
                      : "+ Add To Cart"
                }

              </button>
            )
          }

        </div>

      </div>

    </div>
  )
}

export default ProductDetails
